import React from 'react';
import { useParams } from 'react-router-dom';
import { Mail, NotebookPen, Phone, User, Calendar } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import { PatientDetails, setPatientDetails } from '../../redux/slices/appointmentSlice';
import { formatPhoneInput, isValidEmail, PHONE_REGEX, getLegalUrl } from '../../utils/helpers';
import { LEGAL_PAGES } from '../../utils/constants';
import { DobField } from '../ui/DobField';
import { PaymentInformation } from './PaymentInformation';

type Field = keyof PatientDetails;

export const NewPatientForm: React.FC = () => {
  const dispatch = useDispatch();
  const patientDetails = useSelector((state: RootState) => state.appointment.patientDetails);
  const collectInsurance = useSelector((state: RootState) => state.auth.practice?.collect_insurance_details);
  const practiceTitle = useSelector((state: RootState) => state.auth.practice?.title) || 'our practice';
  const { practiceId } = useParams<{ practiceId: string }>();

  const update = (field: Field, value: string) => {
    dispatch(setPatientDetails({ ...patientDetails, [field]: value }));
  };

  const handlePhoneChange = (value: string) => {
    update('phone', formatPhoneInput(value));
  };

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4 gap-y-2">
        <div className="form-control">
          <label className="label">
            <span className="label-text font-semibold text-gray-500">
              First Name <span className="text-error">*</span>
            </span>
          </label>
          <label className="input input-bordered border-teamcare-blue flex items-center gap-2 bg-white text-black">
            <User size={18} className="text-gray-400" />
            <input
              type="text"
              className="grow placeholder-gray-400"
              placeholder="John"
              value={patientDetails.firstName}
              onChange={(e) => update('firstName', e.target.value)}
            />
          </label>
        </div>

        <div className="form-control">
          <label className="label">
            <span className="label-text font-semibold text-gray-500">
              Last Name <span className="text-error">*</span>
            </span>
          </label>
          <label className="input input-bordered flex border-teamcare-blue items-center gap-2 bg-white text-black">
            <User size={18} className="text-gray-400" />
            <input
              type="text"
              className="grow bg-transparent text-black placeholder-gray-400"
              placeholder="Doe"
              value={patientDetails.lastName}
              onChange={(e) => update('lastName', e.target.value)}
            />
          </label>
        </div>

        <div className="form-control">
          <label className="label">
            <span className="label-text font-semibold text-gray-500">
              Date of Birth <span className="text-error">*</span>
            </span>
          </label>
          <DobField icon={Calendar} value={patientDetails.birth_date} onChange={(iso) => update('birth_date', iso)} />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2">
        <div className="form-control">
          <label className="label">
            <span className="label-text font-semibold text-gray-500">
              Phone Number <span className="text-error">*</span>
            </span>
          </label>
          <label className={`input input-bordered flex items-center gap-2 bg-white text-black ${patientDetails.phone && !PHONE_REGEX.test(patientDetails.phone) ? 'border-error focus-within:border-error focus-within:ring-error' : 'border-teamcare-blue focus-within:border-teamcare-blue focus-within:ring-teamcare-blue'}`}>
            <Phone size={18} className="text-gray-400" />
            <input
              type="text"
              className="grow bg-transparent text-black placeholder-gray-400"
              placeholder="(XXX)XXX-XXXX"
              value={patientDetails.phone}
              onChange={(e) => handlePhoneChange(e.target.value)}
              maxLength={13}
              inputMode="numeric"
            />
          </label>
          {patientDetails.phone && !PHONE_REGEX.test(patientDetails.phone) && (
            <label className="label">
              <span className="label-text-alt text-error">Phone number must be 10 digits</span>
            </label>
          )}
        </div>

        <div className="form-control">
          <label className="label">
            <span className="label-text font-semibold text-gray-500">
              Email <span className="text-error">*</span>
            </span>
          </label>
          <label className={`input input-bordered flex items-center gap-2 bg-white text-black ${patientDetails.email && !isValidEmail(patientDetails.email) ? 'border-error focus-within:border-error focus-within:ring-error' : 'border-teamcare-blue focus-within:border-teamcare-blue focus-within:ring-teamcare-blue'}`}>
            <Mail size={18} className="text-gray-400" />
            <input
              type="email"
              className="grow bg-transparent text-black placeholder-gray-400"
              placeholder="john.doe@example.com"
              value={patientDetails.email}
              onChange={(e) => update('email', e.target.value)}
            />
          </label>
          {patientDetails.email && !isValidEmail(patientDetails.email) && (
            <label className="label">
              <span className="label-text-alt text-error">Please enter a valid email address</span>
            </label>
          )}
        </div>
      </div>

      <div className={`grid grid-cols-1 gap-x-4 gap-y-2 ${collectInsurance ? 'md:grid-cols-2' : ''}`}>
        <div className="form-control h-full">
          <label className="label">
            <span className="label-text font-semibold text-gray-500">Appointment Notes</span>
          </label>
          <div className="relative flex-1">
            <div className="pointer-events-none absolute left-3 top-3">
              <NotebookPen size={18} className="text-gray-400" />
            </div>
            <textarea
              className="textarea textarea-bordered w-full md:h-full pl-10 bg-transparent text-black placeholder-gray-400 border-teamcare-blue"
              rows={2}
              placeholder="Add any notes for your appointment"
              value={patientDetails.notes || ''}
              onChange={(e) => update('notes', e.target.value)}
            />
          </div>
        </div>

        {collectInsurance && (
          <div className="form-control">
            <PaymentInformation />
          </div>
        )}
      </div>

      <div className="form-control pt-1">
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            className="checkbox checkbox-sm border-teamcare-blue mt-1 shrink-0"
            checked={Boolean(patientDetails.smsConsent)}
            onChange={(e) => dispatch(setPatientDetails({ ...patientDetails, smsConsent: e.target.checked }))}
          />
          <span className="text-sm text-gray-600 leading-relaxed">
            I agree to receive text messages from {practiceTitle} about my
            appointments, including confirmations, reminders, and follow-ups.
            Message frequency varies. Message and data rates may apply. Reply
            STOP to opt out, HELP for help. See our{' '}
            <a
              href={getLegalUrl(practiceId, LEGAL_PAGES.privacyPolicy)}
              target="_blank"
              rel="noopener noreferrer"
              className="text-teamcare-blue underline"
            >
              Privacy Policy
            </a>{' '}
            and{' '}
            <a
              href={getLegalUrl(practiceId, LEGAL_PAGES.termsOfUse)}
              target="_blank"
              rel="noopener noreferrer"
              className="text-teamcare-blue underline"
            >
              Terms
            </a>
            .
          </span>
        </label>
      </div>
    </div>
  );
};

